import { canEditTicket } from "@common/utils/user";
import { MeetingInstance } from "@services/meetings/types";
import { Ticket, TicketDetails, TicketIssue } from "@services/tickets/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React, {
  createContext,
  PropsWithChildren,
  Reducer,
  useEffect,
  useReducer,
} from "react";

export type TicketToUpdate = Omit<Partial<Ticket>, "issue"> & {
  issue?: Partial<TicketIssue>;
};
export enum TimeValue {
  FewSeconds,
  NoClue,
  EnterDate,
}
type TimeOfIssue = { type: TimeValue; date: number };
export interface IFormContext {
  isValid: boolean;
  dispatchTicket: (changes: TicketToUpdate) => void;
  dispatchDetails: (changes: Partial<TicketDetails>) => void;
  dispatchMeeting: (changes: Partial<MeetingInstance>) => void;
  canEdit: boolean;
  form: {
    ticket: TicketToUpdate;
    details: Partial<TicketDetails>;
    meeting: Partial<MeetingInstance>;
    hasChanged: boolean;
  };
  timeOfIssue: TimeOfIssue;
  dispatchTimeOfIssue: (changes: Partial<TimeOfIssue>) => void;
  reset: () => void;
  dispatchHasChanged: (hasChanged: boolean) => void;
}
type ReducerState = IFormContext["form"] & Pick<IFormContext, "timeOfIssue">;

const defaultState = {
  ticket: {},
  details: {},
  meeting: {},
  hasChanged: false,
  timeOfIssue: { type: TimeValue.FewSeconds, date: Date.now() },
};
const defaultContext: IFormContext = {
  isValid: false,
  dispatchTicket: () => {},
  dispatchDetails: () => {},
  dispatchMeeting: () => {},
  canEdit: false,
  form: { ticket: {}, details: {}, meeting: {}, hasChanged: false },
  reset: () => {},
  timeOfIssue: { type: TimeValue.FewSeconds, date: Date.now() },
  dispatchTimeOfIssue: () => {},
  dispatchHasChanged: () => {},
};

export const FormContext = createContext<IFormContext>(defaultContext);

const hasSubject = (form: {
  ticket: TicketToUpdate;
  details: Partial<TicketDetails>;
}) => form.ticket.subject !== undefined && form.ticket.subject.length > 0;

const hasDescription = (form: {
  ticket: TicketToUpdate;
  details: Partial<TicketDetails>;
}) =>
  form.details.description !== undefined && form.details.description.length > 0;

const hasIssueType = (form: {
  ticket: TicketToUpdate;
  details: Partial<TicketDetails>;
}) =>
  form.ticket?.issue?.issue !== undefined && form.ticket.issue.issue.length > 0;

const hasDeviceOrRoomOrMeeting = (form: {
  ticket: TicketToUpdate;
  details: Partial<TicketDetails>;
}) => {
  return (
    form.ticket.device !== undefined ||
    (form.ticket.room !== undefined && form.ticket.id !== undefined) ||
    (form.ticket.account !== undefined && form.ticket.id !== undefined) ||
    (form.ticket.meetings !== undefined && form.ticket.meetings.length > 0)
  );
};

const hasTimeOfIssue = (form: {
  ticket: TicketToUpdate;
  details: Partial<TicketDetails>;
  timeOfIssue: TimeOfIssue;
}) => {
  const { timeOfIssue } = form;

  return !isNaN(timeOfIssue.date);
};

const tests = [
  hasSubject,
  hasDescription,
  hasIssueType,
  hasDeviceOrRoomOrMeeting,
  hasTimeOfIssue,
];
const testForm = (form: {
  ticket: TicketToUpdate;
  details: Partial<TicketDetails>;
  timeOfIssue: TimeOfIssue;
}) => tests.reduce((acc, fn) => acc && fn(form), true);

export const FormContextProvider = (
  props: PropsWithChildren<{
    ticket?: TicketToUpdate;
    meeting?: MeetingInstance;
    reset: () => {};
    openDrawer?: boolean;
    hasChanged?: boolean;
  }>
) => {
  const [user] = useAuthenticatedUser();
  const canEdit = canEditTicket(user);
  const [state, dispatch] = useReducer(
    (
      state: ReducerState,
      action: "reset" | Partial<ReducerState>
    ): ReducerState => {
      if (action === "reset") return { ...defaultState };

      const updatedState = (
        Object.keys(state) as (keyof ReducerState)[]
      ).reduce(
        (acc, key) =>
          key === "hasChanged"
            ? acc
            : {
                ...acc,
                [key]: {
                  ...acc[key],
                  ...(action as Partial<ReducerState>)[key],
                },
              },
        state
      );

      return {
        ...updatedState,
        hasChanged:
          (action as Partial<ReducerState>).hasChanged ?? state.hasChanged,
      };
    },
    { ...defaultState }
  );
  const { ticket, details, meeting, timeOfIssue, hasChanged } = state;
  const getDispatch =
    <K extends keyof ReducerState>(k: K) =>
    (data: Partial<ReducerState[K]>) =>
      dispatch({ [k]: data });

  //clear old data when new drawer is closed
  useEffect(() => {
    if (!props.openDrawer) {
      dispatch("reset");
      props.reset();
    }
  }, [props.openDrawer]);

  return (
    <FormContext.Provider
      value={{
        ...defaultContext,
        isValid: testForm({
          ticket: { ...props.ticket, ...ticket },
          details: { ...props.ticket?.details, ...details },
          timeOfIssue,
        }),
        dispatchTicket: getDispatch("ticket"),
        dispatchDetails: getDispatch("details"),
        dispatchMeeting: getDispatch("meeting"),
        dispatchTimeOfIssue: getDispatch("timeOfIssue"),
        canEdit,
        form: {
          ticket: { ...props.ticket, ...ticket },
          details: { ...props.ticket?.details, ...details },
          meeting: { ...props.meeting, ...meeting },
          hasChanged: hasChanged,
        },
        timeOfIssue,
        reset: props.reset,
        dispatchHasChanged: getDispatch("hasChanged"),
      }}
    >
      {props.children}
    </FormContext.Provider>
  );
};
